import React, { useEffect, useState, useReducer } from 'react';
import {
  DateInput, Label, DayInput, MonthInput, YearInput,
} from '@americanexpress/dls-react';
import PropTypes from 'prop-types';
import { parseHtml, calculateAgeInYears } from '../../utils/util';

const getVal = require('lodash/get');

const InputDate = ({
  fieldId,
  cssProperties,
  fieldValidation,
  fieldLabel,
  dayInputPlaceholder,
  monthInputPlaceholder,
  yearInputPlaceholder,
  langLocale,
  fieldRank,
  validationErrors,
  setValidationErrors,
  setFocus,
  filledFormData,
  setFormSubmissionPayload,
  validations = [],
  errors = {},
  elementOrder = [],
  idsToClear,
  monthDayInTwoDigit = false,
  comments,
  setComments,
  entityToEdit,
  entityComments,
  entityCompositeKey,
  separator = '/',
  dateFormat = ['dayInput', 'monthInput', 'yearInput'],
}) => {
  const [dayValue, setDayValue] = useState('');
  const [monthValue, setMonthValue] = useState('');
  const [yearValue, setYearValue] = useState('');
  const [isValid, setIsValid] = useState(true);
  const [validationErrState, dispatchErrState] = useReducer((prevState, payload) => ({ ...prevState, ...payload }), { validationErr: '', isValid: true });

  const splitDateByOrder = (val) => {
    const dateArr = val.split(separator);
    const dateArrObj = {};
    let dateOrder = elementOrder;
    if (dateFormat.length > 0) {
      dateOrder = dateFormat;
    }
    dateOrder.forEach((elem, index) => {
      dateArrObj[elem] = dateArr[index];
    });
    const newDateArr = [];
    if (dateArrObj.yearInput) {
      newDateArr[2] = dateArrObj.yearInput;
    }
    if (dateArrObj.monthInput) {
      newDateArr[1] = monthDayInTwoDigit && dateArrObj.monthInput < 10 ? dateArrObj.monthInput.replace('0', '') : dateArrObj.monthInput;
    }
    if (dateArrObj.dayInput) {
      newDateArr[0] = monthDayInTwoDigit && dateArrObj.dayInput < 10 ? dateArrObj.dayInput.replace('0', '') : dateArrObj.dayInput;
    }
    return newDateArr.filter(Boolean);
  };

  const handleAutoFilledVal = (preFilledVal) => {
    if (entityComments.length > 0
      && entityCompositeKey
      && Object.keys(entityComments.filter(
        (obj) => obj.compositeKey === entityCompositeKey)).length > 0
      && Object.keys(entityComments.filter(
        (obj) => obj.compositeKey === entityCompositeKey)[0]).includes(fieldId)) {
      // This isValid is for showing red borders for the comments from the review tool.
      setIsValid(false);
    }

    // can create a date only if all the 3 inputs are present
    if (elementOrder.length === 3) {
      const dateArr = splitDateByOrder(preFilledVal);
      const date = new Date(dateArr[2], dateArr[1] - 1, dateArr[0]);
      setDayValue(date.getDate());
      setMonthValue(date.getMonth() + 1);
      setYearValue(date.getFullYear());
    } else if (elementOrder.length === 2) {
      const date = splitDateByOrder(preFilledVal);
      if (elementOrder.indexOf('yearInput') >= 0 && elementOrder.indexOf('monthInput') >= 0) {
        setMonthValue(date[0]);
        setYearValue(date[1]);
      } else {
        setDayValue(date[0]);
        setMonthValue(date[1]);
      }
    }
  };

  const setDefaultVal = () => {
    setDayValue('');
    setMonthValue('');
    setYearValue('');
  };

  useEffect(() => {
    // prefill values if values are present in payload (back button support)
    if (filledFormData[fieldId]) {
      handleAutoFilledVal(filledFormData[fieldId]);
    } else if (typeof entityToEdit[fieldId] === 'undefined') {
      setDefaultVal();
    }
  }, [fieldId]);

  useEffect(() => {
    setIsValid(true);
    if (entityToEdit && entityToEdit[fieldId]) {
      handleAutoFilledVal(entityToEdit[fieldId]);
    } else if (typeof filledFormData[fieldId] === 'undefined') {
      setDefaultVal();
    }
  }, [entityToEdit]);

  useEffect(() => {
    if (comments && comments[fieldId]) {
      setIsValid(false);
    }
  }, [comments]);

  const setValOrder = (year, month, day) => {
    let inputDateVal = '';
    const inputValOrder = [];
    if ((year !== '' && month !== '' && day !== '')
      || (year !== '' && month !== '')
      || (day !== '' && month !== '')
    ) {
      let dateOrder = elementOrder;
      if (dateFormat.length > 0) {
        dateOrder = dateFormat;
      }
      dateOrder.forEach((elem) => {
        if (elem === 'yearInput') {
          inputValOrder.push(year);
        } else if (elem === 'monthInput') {
          inputValOrder.push(monthDayInTwoDigit && month < 10 ? `0${month}` : month);
        } else if (elem === 'dayInput') {
          inputValOrder.push(monthDayInTwoDigit && day < 10 ? `0${day}` : day);
        }
      });
      inputDateVal = inputValOrder.join(separator);
    }
    return inputDateVal;
  };

  /* eslint-disable-next-line complexity -- desc */
  const handleOnChange = (e, eventType) => {
    // add max and min date validations
    let day = dayValue;
    let month = monthValue;
    let year = yearValue;
    if (eventType === 'day') {
      day = e.target.value;
      setDayValue(day);
    } else if (eventType === 'month') {
      month = e.target.value;
      setMonthValue(month);
    } else if (eventType === 'year') {
      year = e.target.value;
      setYearValue(year);
    }
    // get these error messages from json
    if (fieldValidation.required && elementOrder.indexOf('dayInput') >= 0 && !day) {
      dispatchErrState({ validationErr: errors.mandatoryDay, isValid: false });
    } else if (fieldValidation.required && elementOrder.indexOf('monthInput') >= 0 && !month) {
      dispatchErrState({ validationErr: errors.mandatoryMonth, isValid: false });
    } else if (fieldValidation.required && elementOrder.indexOf('yearInput') >= 0 && (!year || !/^\d{4}$/.test(year))) {
      dispatchErrState({ validationErr: errors.mandatoryYear, isValid: false });
    } else {
      /* eslint-disable no-lonely-if -- need */
      // if all 3 are present, then create a date object
      if (elementOrder.length === 3) {
        const date = new Date(year, month - 1, day);
        // to add -- min date and max date validations
        if (validations.length > 0) {
          validations.forEach((operation) => {
            if (operation.type === 'calculateAgeInYears') {
              const age = calculateAgeInYears(date);
              if (operation.minAge && age < operation.minAge) {
                dispatchErrState({ validationErr: operation.minAgeError, isValid: false });
                // delete filledFormData[operation.addAs];
              } else if (operation.maxAge && age > operation.maxAge) {
                dispatchErrState({ validationErr: operation.maxAgeError, isValid: false });
                // delete filledFormData[operation.addAs];
              } else {
                dispatchErrState({ validationErr: '', isValid: true });
              }
              filledFormData[operation.addAs] = age;
            }
          });
        } else {
          dispatchErrState({ validationErr: '', isValid: true });
        }

        const inputDateVal = setValOrder(year, month, day);
        setFormSubmissionPayload({
          ...filledFormData,
          [fieldId]: inputDateVal, // date.toLocaleDateString(),
          elemId: null,
        });
      } else if (elementOrder.length === 2) {
        let date = '';
        if (month && year) {
          date = setValOrder(year, month, day);
        } else if (day && month) {
          date = setValOrder(year, month, day);
        }
        setFormSubmissionPayload({ ...filledFormData, [fieldId]: date, elemId: null });
        dispatchErrState({ validationErr: '', isValid: true });
      }
      /* eslint-enable no-lonely-if -- need */
    }

    // Remove comment
    if (comments && comments[fieldId]) {
      delete comments[fieldId];
      setComments({ ...comments });
    }

    setIsValid(true);
  };

  const handleInvalid = (e) => {
    if (fieldRank) {
      e.preventDefault();
      // get these error messages from json
      dispatchErrState({ validationErr: errors.invalidDate, isValid: false });
      let focusEle = '';
      if (elementOrder.indexOf('dayInput') >= 0 && !dayValue) {
        focusEle = 'day';
      } else if (elementOrder.indexOf('dayInput') >= 0 && !monthValue) {
        focusEle = 'month';
      } else if (elementOrder.indexOf('dayInput') >= 0 && (!yearValue || !/^\d{4}$/.test(yearValue))) {
        focusEle = 'year';
      }
      setFocus({ ...validationErrors, ...{ [fieldRank]: `${fieldId}-${focusEle}` } });
    }
  };

  useEffect(() => {
    if (fieldRank) {
      if (validationErrState.isValid && validationErrors[fieldRank]) {
        delete validationErrors[fieldRank];
        setValidationErrors({ ...validationErrors });
      } else if (!validationErrState.isValid) {
        if (!dayValue) {
          setValidationErrors({ ...validationErrors, ...{ [fieldRank]: `${fieldId}-day` } });
        } else if (!monthValue) {
          setValidationErrors({ ...validationErrors, ...{ [fieldRank]: `${fieldId}-month` } });
        } else {
          setValidationErrors({ ...validationErrors, ...{ [fieldRank]: `${fieldId}-year` } });
        }
      }
    }
  }, [validationErrState]);

  useEffect(() => {
    if (getVal(idsToClear.ids, fieldId)) {
      setDayValue('');
      setMonthValue('');
      setYearValue('');
    }
  }, [idsToClear.toggleState]);

  const getElement = (element) => {
    if (element === 'dayInput') {
      return (
        <DayInput
          id={`${fieldId}-day`}
          name={`${fieldId}-day`}
          label={parseHtml(fieldLabel.dayLabel)}
          locale={langLocale}
          value={dayValue}
          className="label-3"
          onChange={(e) => handleOnChange(e, 'day')}
          placeholder={dayInputPlaceholder}
          onInvalid={(e) => handleInvalid(e, 'day')}
          required={fieldValidation && fieldValidation.required ? fieldValidation.required : false}
          style={{
            border: !isValid ? '.0625rem solid red' : '.0625rem solid #8e9092',
          }}
        />
      );
    }
    if (element === 'monthInput') {
      return (
        <MonthInput
          id={`${fieldId}-month`}
          name={`${fieldId}-month`}
          label={parseHtml(fieldLabel.monthLabel)}
          locale={langLocale}
          value={monthValue}
          onChange={(e) => handleOnChange(e, 'month')}
          placeholder={monthInputPlaceholder}
          onInvalid={(e) => handleInvalid(e, 'month')}
          required={fieldValidation && fieldValidation.required ? fieldValidation.required : false}
          style={{
            border: !isValid ? '.0625rem solid red' : '.0625rem solid #8e9092',
          }}
        />
      );
    }
    return (
      <YearInput
        id={`${fieldId}-year`}
        name={`${fieldId}-year`}
        label={parseHtml(fieldLabel.yearLabel)}
        locale={langLocale}
        value={yearValue}
        onChange={(e) => handleOnChange(e, 'year')}
        placeholder={yearInputPlaceholder}
        onInvalid={(e) => handleInvalid(e, 'year')}
        required={fieldValidation && fieldValidation.required ? fieldValidation.required : false}
        style={{
          border: !isValid ? '.0625rem solid red' : '.0625rem solid #8e9092',
        }}
      />
    );
  };

  return (
    <div className={cssProperties.style}>
      <Label
        htmlFor={`date-input-${fieldId}`}
        id={`label-${fieldId}`}
        locale={langLocale}
      >
        {parseHtml(fieldLabel.label)}
        {!fieldValidation.required && (
          <span
            className={fieldLabel.cssProperties.style}
          >
            {fieldLabel && Object.prototype.hasOwnProperty.call(fieldLabel, 'optionalLabel') ? fieldLabel.optionalLabel : ' (Optional)'}
          </span>
        )}
      </Label>
      <DateInput
        id={fieldId}
        labelId={`label-${fieldId}`}
        locale={langLocale}
        onChange={() => { }}
      >
        {
          elementOrder.map((element) => getElement(element))
        }
      </DateInput>
      {
        validationErrState.validationErr
        && (
          <div id="alert-error" className={cssProperties.validationErrorStyle ? cssProperties.validationErrorStyle : 'alert dls-color-warning pad-1-t col-md-12'}>
            <span className="icon icon-sm dls-icon-warning-filled pad-1-r" />
            <span>{validationErrState.validationErr}</span>
          </div>
        )
      }
      {
        comments && comments[fieldId]
        && (
          <div id="alert-error" className="alert dls-color-warning pad-1-t">
            <span className="icon icon-sm dls-icon-warning-filled pad-1-r" />
            <span>{comments[fieldId].comment}</span>
          </div>
        )
      }
      {
        entityComments.length > 0
        && entityCompositeKey
        && Object.keys(entityComments.filter(
          (obj) => obj.compositeKey === entityCompositeKey)).length > 0
        && Object.keys(entityComments.filter(
          (obj) => obj.compositeKey === entityCompositeKey)[0]).includes(fieldId)
        && monthValue && !isValid
        && (
          <div id="alert-error" className="alert dls-color-warning pad-1-t">
            <span className="icon icon-sm dls-icon-warning-filled pad-1-r" />
            <span>{
              fieldId === 'compositeKey' ? entityComments.filter((obj) => obj.compositeKey === entityCompositeKey)[0][fieldId]
                : entityComments.filter(
                  (obj) => obj.compositeKey === entityCompositeKey)[0][fieldId].defaultValue
            }
            </span>
          </div>
        )
      }
    </div>
  );
};
InputDate.propTypes = {
  fieldId: PropTypes.string,
  cssProperties: PropTypes.objectOf(PropTypes.shape),
  fieldLabel: PropTypes.objectOf(PropTypes.shape),
  dayInputPlaceholder: PropTypes.string,
  monthInputPlaceholder: PropTypes.string,
  yearInputPlaceholder: PropTypes.string,
  langLocale: PropTypes.string,
  fieldValidation: PropTypes.objectOf(PropTypes.shape),
  fieldRank: PropTypes.number,
  validationErrors: PropTypes.objectOf(PropTypes.shape),
  setValidationErrors: PropTypes.func,
  setFocus: PropTypes.func,
  filledFormData: PropTypes.objectOf(PropTypes.shape),
  setFormSubmissionPayload: PropTypes.func,
  validations: PropTypes.arrayOf(PropTypes.shape),
  errors: PropTypes.objectOf(PropTypes.shape),
  elementOrder: PropTypes.arrayOf(PropTypes.string),
  idsToClear: PropTypes.objectOf(PropTypes.shape),
  monthDayInTwoDigit: PropTypes.string,
  comments: PropTypes.objectOf(PropTypes.shape),
  setComments: PropTypes.func,
  entityToEdit: PropTypes.objectOf(PropTypes.shape),
  entityComments: PropTypes.objectOf(PropTypes.shape),
  entityCompositeKey: PropTypes.string,
  separator: PropTypes.string,
  dateFormat: PropTypes.arrayOf(PropTypes.string),
};
export default InputDate;
